import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useEstimators } from '@/features/leads/hooks/useEstimators';
import { createJobFromLead } from '../api/jobs-api';
import type { Lead } from '@/features/leads/types';
import type { CreateJobFromLeadCommand } from '../types';

interface CreateJobDialogProps {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJobCreated: () => void;
}

export function CreateJobDialog({ lead, open, onOpenChange, onJobCreated }: CreateJobDialogProps) {
  const { estimators } = useEstimators();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateJobFromLeadCommand>({
    propertyAddress1: lead.address1,
    propertyAddress2: lead.address2 ?? '',
    propertyCity: lead.city,
    propertyState: lead.state,
    propertyZip: lead.zip,
    assignedToUserId: lead.assignedToUserId ?? undefined,
    description: '',
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createJobFromLead(lead.id, formData);
      onOpenChange(false);
      onJobCreated();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to create job');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof CreateJobFromLeadCommand, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Job</DialogTitle>
          <DialogDescription>
            Create a new job for {lead.customerName}. Address is pre-filled from the lead.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Property Address</h4>
            <div className="grid gap-2">
              <div>
                <Label htmlFor="address1">Address Line 1</Label>
                <Input
                  id="address1"
                  value={formData.propertyAddress1 ?? ''}
                  onChange={(e) => updateField('propertyAddress1', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="address2">Address Line 2</Label>
                <Input
                  id="address2"
                  value={formData.propertyAddress2 ?? ''}
                  onChange={(e) => updateField('propertyAddress2', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.propertyCity ?? ''}
                    onChange={(e) => updateField('propertyCity', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.propertyState ?? ''}
                    onChange={(e) => updateField('propertyState', e.target.value)}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP</Label>
                  <Input
                    id="zip"
                    value={formData.propertyZip ?? ''}
                    onChange={(e) => updateField('propertyZip', e.target.value)}
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Assignment</h4>
            <div>
              <Label htmlFor="assignedTo">Estimator / PM</Label>
              <Select
                id="assignedTo"
                value={formData.assignedToUserId?.toString() ?? ''}
                onChange={(e) => updateField('assignedToUserId', e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">Select estimator</option>
                {estimators.map((est) => (
                  <option key={est.id} value={est.id.toString()}>
                    {est.firstName} {est.lastName}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              value={formData.description ?? ''}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="e.g., Board-up, Repairs, etc."
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
